import { humaniseEnumKey } from "@/lib/enumLabel";

/**
 * Organization membership roles (organization_members.role) and the ONE
 * user-facing word for each.
 *
 * `super_admin` is "Owner" everywhere a person sees it. Issue #539: the members
 * list rendered the map ("Owner") while Org info humanised the raw enum
 * ("Super Admin"), so one account's single role wore two names on one screen
 * and read as two permission levels. Render roles through orgRoleLabel and
 * never by transforming the enum string inline.
 */
export type OrgRole = "super_admin" | "admin" | "editor";

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  super_admin: "Owner",
  admin: "Admin",
  editor: "Editor",
};

export const ORG_ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  super_admin: "Full control — manage members, billing, settings, and content",
  admin: "Manage team, content, and profile — no billing or member removal",
  editor: "Edit content and profile only",
};

const isOrgRole = (value: string): value is OrgRole => value in ORG_ROLE_LABELS;

/**
 * The display label for a membership role. Unmapped values are humanised
 * rather than dropped (see enumLabel.ts); a missing role renders as a dash.
 */
export function orgRoleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return isOrgRole(role) ? ORG_ROLE_LABELS[role] : humaniseEnumKey(role);
}
